<template>
    <div class="flex flex-col flex-grow">
        <transition name="fade">
            <div id="loading-indicator" class="text-leonard-green" v-if="pageLoading">
                <Logo class="h-12 w-auto sm:h-20"></Logo>
            </div>
        </transition>

        <Header></Header>
        <DynamicBanner
            :banner="banner"
            v-on:toggleBannerModal="toggleBannerModal"
            v-on:removeModal="removeModal = true"
        ></DynamicBanner>

        <main class="relative flex-grow">
            <router-view></router-view>
        </main>

        <Footer></Footer>

        <Cart v-if="$auth.check()"></Cart>
        <div class="cart-overlay" @click="closeCart"></div>

        <BannerModal
            :show="showModal"
            :header="banner.modal_header"
            :body="banner.modal_body"
            v-on:toggleBannerModal="toggleBannerModal"
            :class="{'fixed pb-6': !hiddenOverlay}"
            v-if="!removeModal"
        ></BannerModal>

        <!-- Modal for confirmation -->
        <ConfirmationModal
            :class="{'fixed': confirmationDialog.show}"
            :show="confirmationDialog.show"
            :header="confirmationDialog.header"
            :body="confirmationDialog.body"
            :cancelText="confirmationDialog.cancelText"
            :confirmText="confirmationDialog.confirmText"
            v-on:confirm="confirmed"
            v-on:cancel="confirmationDialog.show = false"
        ></ConfirmationModal>

        <Feedback></Feedback>
    </div>
</template>

<script>
import Cart from './components/app/Cart';
import Header from './components/app/Header';
import Footer from './components/app/Footer';
import BannerModal from "./components/app/BannerModal";
import ConfirmationModal from "./components/app/ConfirmationModal";
import Feedback from "./components/FeedbackContainer.vue";

import Content from "./classes/Content";
import DynamicBanner from "./components/partials/DynamicBanner.vue";
import Logo from "./components/icons/Logo.vue";

function debounce(func, wait) {
    let timeout;
    return function(...args) {
        const context = this;
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(context, args), wait);
    };
}

export default {
    data() {
        return {
            Content: new Content(),
            banner: {
                show: false,
            },
            confirmationDialog: {
                show: false,
                header: null,
                body: null,
                confirmText: 'Confirm',
                cancelText: 'Cancel',
                context: null,
            },
            hiddenOverlay: true,
            showModal: false,
            removeModal: false,
            navOpen: false,
            pageLoading: true, // Set to true by default for first mount
            lastScrollY: 0,
        }
    },

    components: {
        Logo,
        DynamicBanner,
        Cart,
        Header,
        Footer,
        BannerModal,
        ConfirmationModal,
        Feedback
    },

    mounted() {
        const debouncedHandleScroll = debounce(this.handleScroll.bind(this), 200);
        window.addEventListener('scroll', debouncedHandleScroll, { passive: true });
        window.addEventListener('touchmove', debouncedHandleScroll, { passive: true });
        let self = this;
        this.getBanner();

        setTimeout(() => { // Hide loading after first mount
            self.pageLoading = false
        }, 750);

        // Show loading before leaving the route
        this.$router.beforeEach((to, from, next) => {
            if (!to.hash) self.pageLoading = true;

            setTimeout(() => {
                next();
            }, 750);
        });

        window.events.$on('finishedLoading', () => {
            self.pageLoading = false;
        })

        window.events.$on('updateBanner', () => {
            self.getBanner()
        })

        window.events.$on('showConfirmation', function (details) {
            self.confirmationDialog = details
        })

        this.$router.afterEach((to, from) => {
            if (to.meta.title) {
                document.title = to.meta.title(to) + " - Leonard's Studio";
            }
        });
    },

    methods: {
        handleScroll(event) {
            if (event.type === 'scroll' || event.type === 'touchmove') {
                const topMenu = document.querySelector('[data-menu="top"]');
                const header = document.querySelector('.sticky');
                const currentScrollY = window.scrollY;
                const isSticky = currentScrollY > 0;
                const scrollThreshold = 5; // Adjust this value as needed
                const scrollDirection = currentScrollY > this.lastScrollY + scrollThreshold ? 'down' :
                    currentScrollY < this.lastScrollY - scrollThreshold ? 'up' :
                        null;

                if (scrollDirection) {
                    this.lastScrollY = currentScrollY;

                    if (header) {
                        header.classList.toggle('shadow-md', isSticky);
                        header.setAttribute('data-scroll-direction', scrollDirection);
                        header.style.top = scrollDirection === 'down' ? `-${topMenu?.offsetHeight || 16}px` : '0';
                    }
                }
            }
        },

        getBanner() {
            let self = this;

            this.Content.getBanner().then(function (response) {
                self.banner = {
                    show: response.data.banner_show,
                    short_text: response.data.banner_short_text,
                    long_text: response.data.banner_long_text,
                    button_text: response.data.banner_button_text,
                    modal_header: response.data.banner_modal_header,
                    modal_body: response.data.banner_modal_body
                };
            });
        },

        closeCart() {
            window.events.$emit('toggleCart');
        },

        toggleBannerModal(val) {
            if (val === true) {
                this.hiddenOverlay = false;
            }

            this.showModal = val;

            if (val === false) {
                let self = this;
                this.$nextTick(self.delay(() => {
                    self.hiddenOverlay = true;
                }, 250));
            }
        },

        confirmed() {
            let self = this;
            this.confirmationDialog.show = false;
            window.events.$emit('confirmed', self.confirmationDialog.context)
        },
    },
}
</script>

<style lang="scss" scoped>
.fade-enter-active, .fade-leave-active {
    transition: opacity .4s;
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */
{
    opacity: 0;
}

#loading-indicator {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #FFF;
    z-index: 1999;
    pointer-events: none;

    img, svg {
        position: absolute;
        top: -180px;
        left: 0;
        right: 0;
        bottom: 0;
        margin: auto;
    }

    &::before {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        margin: auto;
        border-radius: 50%;
        border: 4px solid var(--leonard-green);
        content: '';
    }

    &::before {
        max-width: 50px;
        max-height: 50px;
        border-right-color: var(--leonard-grey);
        animation: rotation 2s linear infinite;
    }
}

@keyframes rotation {
    0% {
        transform: rotate(0deg);
    }
    50% {
        transform: rotate(180deg);
    }
    100% {
        transform: rotate(360deg);
    }
}
</style>
